import React from 'react'
import { cn } from '../../../lib/utils'
import { Progress } from '../../../components/ui/Progress'
import { Badge } from '../../../components/ui/Badge'
import { Card, CardContent, CardHeader } from '../../../components/ui/Card'
import { AutosaveIndicator } from '../../../components/ui/AutosaveIndicator'
import { 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Brain,
  Target,
  Users,
  TrendingUp,
  BarChart3,
  Shield,
  Settings,
  Package,
  Award
} from 'lucide-react'

interface ProgressStepData {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  businessValue: string
  estimatedTime: string
  status: 'completed' | 'current' | 'upcoming' | 'skipped'
  completionQuality?: 'excellent' | 'good' | 'basic' | 'incomplete'
  valuePoints?: number
}

interface EnterpriseProgressBarProps {
  currentScreen: string
  totalProgress: number
  hasValidationErrors?: boolean
  className?: string
  lastSaved?: Date | string | null
  isSaving?: boolean
  saveError?: boolean
}

const screenConfigs: Record<string, ProgressStepData> = {
  'industry-selection': {
    id: 'industry',
    title: 'Industry Selection',
    description: 'Define your market focus and industry context',
    icon: <Target className="h-5 w-5" />,
    businessValue: 'Market-specific intelligence targeting',
    estimatedTime: '2 min',
    status: 'completed',
    completionQuality: 'excellent',
    valuePoints: 15
  },
  'business-profile': {
    id: 'profile',
    title: 'Business Profile',
    description: 'Configure your organizational parameters',
    icon: <Users className="h-5 w-5" />,
    businessValue: 'Personalized intelligence recommendations',
    estimatedTime: '3 min',
    status: 'completed',
    completionQuality: 'good',
    valuePoints: 20
  },
  'revenue-intelligence': {
    id: 'revenue',
    title: 'Revenue Intelligence',
    description: 'Set up revenue tracking and market analysis',
    icon: <TrendingUp className="h-5 w-5" />,
    businessValue: 'Revenue optimization insights',
    estimatedTime: '4 min',
    status: 'current',
    valuePoints: 25
  },
  'supply-chain-intelligence': {
    id: 'supply',
    title: 'Supply Chain Intelligence',
    description: 'Configure supply chain monitoring and risk assessment',
    icon: <BarChart3 className="h-5 w-5" />,
    businessValue: 'Supply chain risk mitigation',
    estimatedTime: '3 min',
    status: 'upcoming',
    valuePoints: 20
  },
  'competitive-intelligence': {
    id: 'competitive',
    title: 'Competitive Intelligence',
    description: 'Enable competitor monitoring and market positioning',
    icon: <Shield className="h-5 w-5" />,
    businessValue: 'Competitive advantage insights',
    estimatedTime: '4 min',
    status: 'upcoming',
    valuePoints: 25
  },
  'service-selection': {
    id: 'service',
    title: 'Service Selection',
    description: 'Choose your intelligence service package',
    icon: <Package className="h-5 w-5" />,
    businessValue: 'Tailored intelligence services',
    estimatedTime: '3 min',
    status: 'upcoming',
    valuePoints: 20
  },
  'intelligence-delivery': {
    id: 'delivery',
    title: 'Intelligence Delivery',
    description: 'Configure how you receive intelligence insights',
    icon: <Settings className="h-5 w-5" />,
    businessValue: 'Automated intelligence delivery',
    estimatedTime: '2 min',
    status: 'upcoming',
    valuePoints: 15
  },
  'payment-activation': {
    id: 'payment',
    title: 'Payment & Activation',
    description: 'Complete payment and activate your service',
    icon: <Package className="h-5 w-5" />,
    businessValue: 'Service activation and delivery',
    estimatedTime: '3 min',
    status: 'upcoming',
    valuePoints: 10
  },
  'intelligence-summary': {
    id: 'summary',
    title: 'Intelligence Summary',
    description: 'Review and finalize your intelligence configuration',
    icon: <Award className="h-5 w-5" />,
    businessValue: 'Complete strategic intelligence platform',
    estimatedTime: '3 min',
    status: 'upcoming',
    valuePoints: 35
  }
}

const getQualityColor = (quality?: string) => {
  switch (quality) {
    case 'excellent': return 'text-green-600 bg-green-50 border-green-200'
    case 'good': return 'text-blue-600 bg-blue-50 border-blue-200'
    case 'basic': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    case 'incomplete': return 'text-red-600 bg-red-50 border-red-200'
    default: return 'text-gray-600 bg-gray-50 border-gray-200'
  }
}

const getStatusIcon = (status: string, quality?: string) => {
  switch (status) {
    case 'completed':
      if (quality === 'excellent') return <CheckCircle2 className="h-4 w-4 text-green-600" />
      if (quality === 'good') return <CheckCircle2 className="h-4 w-4 text-blue-600" />
      if (quality === 'basic') return <CheckCircle2 className="h-4 w-4 text-yellow-600" />
      return <CheckCircle2 className="h-4 w-4 text-green-600" />
    case 'current':
      return <Clock className="h-4 w-4 text-blue-600 animate-pulse" />
    case 'upcoming':
      return <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
    default:
      return <AlertCircle className="h-4 w-4 text-gray-400" />
  }
}


export const EnterpriseProgressBar: React.FC<EnterpriseProgressBarProps> = ({
  currentScreen,
  totalProgress,
  hasValidationErrors = false,
  className,
  lastSaved,
  isSaving = false,
  saveError = false
}) => {

  const currentStep = screenConfigs[currentScreen]
  
  // Calculate estimated time remaining
  const allSteps = Object.values(screenConfigs)
  const upcomingSteps = allSteps.filter(step => step.status === 'upcoming' || step.status === 'current')
  const estimatedTimeRemaining = upcomingSteps.reduce((total, step) => {
    const timeInMinutes = parseInt(step.estimatedTime.replace(' min', ''))
    return total + timeInMinutes
  }, 0)

  // Get current step number
  const currentStepNumber = Object.keys(screenConfigs).findIndex(key => key === currentScreen) + 1
  const totalSteps = Object.keys(screenConfigs).length

  // Get validation status for brief view
  const getValidationStatus = () => {
    if (hasValidationErrors) {
      return { icon: <AlertCircle className="h-4 w-4 text-red-500" />, text: 'Needs attention', color: 'text-red-600' }
    }
    return { icon: <CheckCircle2 className="h-4 w-4 text-green-500" />, text: 'Ready to proceed', color: 'text-green-600' }
  }

  const validationStatus = getValidationStatus()

  return (
    <Card className={cn("mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100 transition-all duration-300", className)}>
      {/* Brief View Header - Always Visible */}
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Brain className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Setup Progress
              </h3>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>Step {currentStepNumber} of {totalSteps}: {currentStep?.title}</span>
                <span>•</span>
                <div className="flex items-center gap-1">
                  {validationStatus.icon}
                  <span className={validationStatus.color}>{validationStatus.text}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Brief Progress Info */}
            <div className="text-right">
              <Badge 
                variant="secondary" 
                className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-700 border-blue-200"
              >
                {totalProgress}% Complete
              </Badge>
            </div>
            
          </div>
        </div>

        {/* Brief Progress Bar - Always Visible */}
        <div className="mt-3 space-y-2">
          <Progress value={totalProgress} className="h-2 bg-gray-200" />
          <div className="flex items-center justify-between text-xs text-gray-600">
            <AutosaveIndicator
              lastSaved={lastSaved}
              isSaving={isSaving}
              hasError={saveError}
              className="text-xs text-gray-500"
            />
<span>Configuration Progress</span>
          </div>
        </div>
      </CardHeader>

    </Card>
  )
}